import React, { useState, useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Modal,
  Typography,
  Backdrop,
  Fade,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  IconButton
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import PropTypes from 'prop-types'
import { numberFormat } from 'helpers'

import { connect } from 'react-redux'
import { actionAlert } from 'store/actions/util'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 3, 2),
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  tableContainer: {
    maxHeight: 300,
    overflowY: 'auto'
  },
  title: {
    marginBottom: theme.spacing(2)
  },
  spacer: {
    flexGrow: 1
  },
  copyButton: {
    marginLeft: theme.spacing(1)
  },
  saveButton: {
    marginLeft: theme.spacing(1)
  },
  row: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1)
  },
}))

const ExModalDetail = props => {
  const { open, onClose, transaction, isEdit = false, onSaveEdit } = props
  const classes = useStyles()
  
  const [orders, setOrders] = useState([])
  const [deletedOrderIds, setDeletedOrderIds] = useState([])
  const debounceTimers = useRef({})

  useEffect(() => {
    if (transaction && transaction.orders) {
      setOrders(transaction.orders.map(order => ({ ...order })))
    } else {
      setOrders([])
    }
    setDeletedOrderIds([])

    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer))
      debounceTimers.current = {}
    }
  }, [transaction, open])

  if (!transaction) return null

  const handleQuantityChange = (orderId, value) => {
    let cleanVal = value
    if (typeof value === 'string' && value.length > 1 && value.startsWith('0')) {
      cleanVal = String(parseInt(value, 10) || 0)
    }
    const newQty = parseInt(cleanVal, 10)
    const qty = isNaN(newQty) || newQty < 0 ? 0 : newQty

    setOrders(prevOrders => prevOrders.map(item => {
      if (item.id === orderId) {
        return { ...item, quantity: qty }
      }
      return item
    }))

    if (debounceTimers.current[orderId]) {
      clearTimeout(debounceTimers.current[orderId])
    }

    if (qty === 0) {
      debounceTimers.current[orderId] = setTimeout(() => {
        setOrders(prevOrders => prevOrders.map(item => {
          if (item.id === orderId && (!item.quantity || item.quantity <= 0)) {
            return { ...item, quantity: 1 }
          }
          return item
        }))
      }, 800)
    }
  }

  const handleQuantityBlur = (orderId) => {
    if (debounceTimers.current[orderId]) {
      clearTimeout(debounceTimers.current[orderId])
      delete debounceTimers.current[orderId]
    }
    setOrders(prevOrders => prevOrders.map(item => {
      if (item.id === orderId && (!item.quantity || item.quantity <= 0)) {
        return { ...item, quantity: 1 }
      }
      return item
    }))
  }

  const handleDeleteOrder = (orderId) => {
    if (debounceTimers.current[orderId]) {
      clearTimeout(debounceTimers.current[orderId])
      delete debounceTimers.current[orderId]
    }
    setDeletedOrderIds(prev => [...prev, orderId])
    setOrders(prevOrders => prevOrders.filter(item => item.id !== orderId))
  }

  const calculateTotal = () => {
    return orders.reduce((sum, order) => {
      return sum + (Number(order.price || 0) * Number(order.quantity || 0))
    }, 0)
  }

  const handleSave = () => {
    const payloadOrders = [
      ...orders.map(o => ({ id: o.id, quantity: Math.max(1, Number(o.quantity) || 1) })),
      ...deletedOrderIds.map(id => ({ id: id, delete: true }))
    ]

    if (onSaveEdit) {
      onSaveEdit(transaction.id, { orders: payloadOrders })
    }
    onClose()
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <Typography
            className={classes.title}
            variant="h4"
          >
            {isEdit ? 'Edit Order Transaksi' : 'Detail Transaksi'} ( {transaction ? transaction.code : ''} )
          </Typography>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Nama Produk</TableCell>
                  <TableCell>Jumlah</TableCell>
                  <TableCell>Harga Asli</TableCell>
                  <TableCell>Harga Diskon</TableCell>
                  {isEdit && <TableCell>Aksi</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
              {
                orders && orders.map((order, index) => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={order.id || index}
                  >
                    <TableCell>{index+1}</TableCell>
                    <TableCell>{order.product ? order.product.name : ''}</TableCell>
                    <TableCell>
                      {isEdit ? (
                        <TextField
                          type="number"
                          value={order.quantity}
                          onChange={(e) => {
                            let val = e.target.value
                            if (val && val.length > 1 && val.startsWith('0')) {
                              val = String(parseInt(val, 10) || 0)
                              e.target.value = val
                            }
                            handleQuantityChange(order.id, val)
                          }}
                          onBlur={() => handleQuantityBlur(order.id)}
                          inputProps={{ min: 1, style: { padding: '6px 8px' } }}
                          style={{ width: 70 }}
                        />
                      ) : (
                        order.quantity
                      )}
                    </TableCell>
                    <TableCell>{numberFormat(order.product ? order.product.price : 0, 'Rp. ')}</TableCell>
                    <TableCell>{numberFormat(order.price, 'Rp. ')}</TableCell>
                    {isEdit && (
                      <TableCell>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDeleteOrder(order.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              }
              </TableBody>
            </Table>
          </TableContainer>
          <br/>
          <Typography
            variant="h5"
          >
            Total {numberFormat(isEdit ? calculateTotal() : (transaction ? transaction.total : 0), 'Rp. ')}
          </Typography>
          <div className={classes.row}>
            <span className={classes.spacer} />
            <Button
              color="primary"
              variant="outlined"
              onClick={onClose}
            >
              {isEdit ? 'Batal' : 'Tutup'}
            </Button>
            {isEdit && orders.length > 0 && (
              <Button
                color="primary"
                variant="contained"
                className={classes.saveButton}
                onClick={handleSave}
              >
                Simpan
              </Button>
            )}
          </div>
        </div>
      </Fade>
    </Modal>
  )
}

ExModalDetail.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transaction: PropTypes.object,
  isEdit: PropTypes.bool,
  onSaveEdit: PropTypes.func
}

const mapStateToProps = (state, props) => {
  return {
  }
}

const mapDispatchToProps = (dispatch) => { 
  return {
    dispatchAlert: (status, severity, message) => dispatch(actionAlert(status, severity, message))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ExModalDetail)
